/**
 * What one post row shows beside the post itself, and the ordering the toolbar
 * offers. The route returns the figures per observation; this file selects the
 * latest observation of each and orders rows, and computes nothing else.
 */

import type { UseQueryResult } from '@tanstack/react-query';

import type { PostMetricRow, PostMetrics, PostRow, Sentiment } from '../api/schemas';

export type Reading<T> =
  { status: 'loading' } | { status: 'failed' } | { status: 'ready'; value: T };

export function readingOf<T, U>(query: UseQueryResult<T, Error>, read: (data: T) => U): Reading<U> {
  if (query.status === 'pending') return { status: 'loading' };
  if (query.status === 'error') return { status: 'failed' };
  return { status: 'ready', value: read(query.data) };
}

export interface Figure {
  value: number | null;
  fidelity: PostMetricRow['fidelity'];
}

/** `null` is a metric the provider returned no observation for. */
export interface PostFigures {
  reach: Figure | null;
  estimatedReach: Figure | null;
  likes: Figure | null;
  comments: Figure | null;
  shares: Figure | null;
  saves: Figure | null;
}

export interface Score {
  score: number;
  n: number;
  interval: readonly [number, number];
}

export interface PostEntry {
  post: PostRow;
  figures: Reading<PostFigures>;
  sentiment: Reading<Score | null>;
}

/** A story carries no figures or sentiment (the route never scores or
 *  measures one), so a story row skips both cells and both queries. */
export function isStory(post: PostRow): boolean {
  return post.kind === 'STORY';
}

export type SortKey = 'recent' | 'reach';

export const SORT_KEYS: SortKey[] = ['recent', 'reach'];

function latest(rows: PostMetricRow[], metric: PostMetricRow['metric']): Figure | null {
  const observed = rows
    .filter((row) => row.metric === metric)
    .sort((a, b) => Date.parse(b.observed_at) - Date.parse(a.observed_at))[0];
  return observed === undefined ? null : { value: observed.value, fidelity: observed.fidelity };
}

export function figuresOf(metrics: PostMetrics): PostFigures {
  return {
    reach: latest(metrics.rows, 'views'),
    estimatedReach: latest(metrics.rows, 'estimated_reach'),
    likes: latest(metrics.rows, 'likes'),
    comments: latest(metrics.rows, 'comments'),
    shares: latest(metrics.rows, 'shares'),
    saves: latest(metrics.rows, 'saves'),
  };
}

/** Whether the post has a reading of the metric. One the network does not
 *  collect for it (a photo's views and likes the author hid, both returned as
 *  null; shares and saves outside a post_stats observation, never returned)
 *  is left out, never drawn as a dash. */
export function collected(figures: PostFigures, metric: keyof PostFigures): boolean {
  const value = figures[metric]?.value;
  return value !== null && value !== undefined;
}

/** The most recent week's score, or `null` when no week has one. */
export function scoreOf(sentiment: Sentiment): Score | null {
  const point = [...sentiment.points].sort((a, b) =>
    b.observed_week.localeCompare(a.observed_week),
  )[0];
  if (point === undefined) return null;
  return { score: point.score, n: point.n, interval: point.confidence_interval };
}

export function reachOf(entry: PostEntry): number | null {
  return entry.figures.status === 'ready' ? (entry.figures.value.reach?.value ?? null) : null;
}

function byPublishedDesc(a: PostEntry, b: PostEntry): number {
  return Date.parse(b.post.published_at) - Date.parse(a.post.published_at);
}

/** A row with no reach reading sorts after every row that has one. */
export function orderPosts(entries: PostEntry[], sort: SortKey): PostEntry[] {
  return [...entries].sort((a, b) => {
    if (sort === 'reach') {
      const reachA = reachOf(a);
      const reachB = reachOf(b);
      if (reachA !== null && reachB !== null && reachA !== reachB) return reachB - reachA;
      if (reachA !== null && reachB === null) return -1;
      if (reachA === null && reachB !== null) return 1;
    }
    return byPublishedDesc(a, b);
  });
}
